import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import axios from 'axios';
import ProfileCompletion from './ProfileCompletion';
import ProfileView from './ProfileView';
import ProfileSignature from './ProfileSignature';
import PasswordChange from './PasswordChange';
import CertificateManagement from './CertificateManagement';
import TrainingManagement from './TrainingManagement';
import CrewHoursBoard from '../ops/CrewDutyRoster/components/CrewHoursBoard';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CREW_HOURS_VIEWER_ROLES = [
  ROLES.OPERATIONS_MANAGER,
  ROLES.OPERATIONS_EXECUTIVE,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.SUPER_ADMIN
];

const STATUS_STEPS = [
  'PENDING_VERIFICATION',
  'EMAIL_VERIFIED',
  'ROLE_VERIFIED',
  'ACTIVATED',
  'PROFILE_COMPLETE',
  'TRAINING_COMPLETE',
  'FULLY_ONBOARDED'
];

const STATUS_LABELS = {
  PENDING_VERIFICATION: 'Pending Verification',
  EMAIL_VERIFIED: 'Email Verified',
  ROLE_VERIFIED: 'Role Verified',
  ACTIVATED: 'Activated',
  PROFILE_COMPLETE: 'Profile Complete',
  TRAINING_COMPLETE: 'Training Complete',
  FULLY_ONBOARDED: 'Fully Onboarded',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled'
};

const OnboardingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState('');
  const [completingTraining, setCompletingTraining] = useState(false);
  const [profileTab, setProfileTab] = useState(0);
  const [displayTimezone, setDisplayTimezone] = useState('Asia/Kolkata');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setDisplayTimezone(res.data.displayTimezone);
      } catch {
        // keep default
      }
    };
    fetchSettings();
  }, []);

  // Check if this is the user's own profile
  const isOwnProfile = useMemo(() => {
    if (!data || !user?.id) {
      console.log('[DEBUG] isOwnProfile: false - no data or user', { hasData: !!data, hasUser: !!user?.id });
      return false;
    }
    
    // Handle both populated userId object and raw userId string/ObjectId
    let onboardingUserId = null;
    if (data.userId) {
      // If userId is populated (object), use _id; otherwise use userId directly
      onboardingUserId = data.userId._id || data.userId;
    }
    
    if (!onboardingUserId) {
      console.log('[DEBUG] isOwnProfile: false - no onboardingUserId', { userId: data.userId });
      return false;
    }
    
    const currentUserId = user.id; // User object from AuthContext uses 'id', not '_id'
    // Convert both to strings for comparison (handles ObjectId, string, etc.)
    const currentUserIdStr = String(currentUserId);
    const onboardingUserIdStr = String(onboardingUserId);
    const isOwn = currentUserIdStr === onboardingUserIdStr;
    
    console.log('[DEBUG] isOwnProfile calculation:', {
      currentUserId: currentUserIdStr,
      onboardingUserId: onboardingUserIdStr,
      isOwn,
      userIdType: typeof data.userId,
      userIdIsObject: typeof data.userId === 'object' && data.userId !== null,
      userIdHasId: data.userId?._id ? 'yes' : 'no',
      userIdValue: data.userId
    });
    return isOwn;
  }, [data, user?.id]);
  
  // Activation is now automatic - no separate activation step needed
  // Only SUPER_ADMIN and ACCOUNTABLE_MANAGER can complete/edit profiles (not regular users)
  const canCompleteProfile = useMemo(() => {
    return [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role)
      && data 
      && ['ACTIVATED', 'PROFILE_COMPLETE', 'TRAINING_COMPLETE', 'FULLY_ONBOARDED'].includes(data.status);
  }, [user?.role, data]);

  const canApprove = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role) && data?.status && !['FULLY_ONBOARDED', 'REJECTED', 'CANCELLED'].includes(data.status);
  const canReject = user?.role === ROLES.SUPER_ADMIN && data && !['REJECTED', 'CANCELLED', 'FULLY_ONBOARDED'].includes(data.status);
  
  // Users can manage their own certificates, SUPER_ADMIN can manage any
  const canManageCertificates = useMemo(() => {
    if (!data || data.status === 'REJECTED') {
      console.log('[DEBUG] canManageCertificates: false - data:', !!data, 'status:', data?.status);
      return false;
    }
    const canManage = isOwnProfile || user?.role === ROLES.SUPER_ADMIN;
    console.log('[DEBUG] Certificate Management:', { 
      isOwnProfile, 
      userRole: user?.role, 
      canManage, 
      status: data.status,
      userId: user?.id,
      onboardingUserId: data.userId?._id || data.userId,
      dataUserIdType: typeof data.userId
    });
    return canManage;
  }, [isOwnProfile, user?.role, data]);

  const profileUserId = data?.userId?._id || data?.userId || null;
  const canManageSignature = Boolean(
    profileUserId &&
    data?.status !== 'REJECTED' &&
    (isOwnProfile || user?.role === ROLES.SUPER_ADMIN)
  );

  const isCrewProfile = useMemo(() => {
    const role = data?.requestedRole || data?.userId?.role;
    return role === ROLES.CREW || role === ROLES.LOAN_PILOT || role === ROLES.EXTERNAL_PILOT;
  }, [data?.requestedRole, data?.userId?.role]);

  // Duty hours board for roster crew (CREW + LOAN_PILOT); EXTERNAL_PILOT is not rostered
  const canViewCrewHours = Boolean(
    ([ROLES.CREW, ROLES.LOAN_PILOT].includes(data?.requestedRole) || [ROLES.CREW, ROLES.LOAN_PILOT].includes(data?.userId?.role)) &&
    profileUserId &&
    (isOwnProfile || CREW_HOURS_VIEWER_ROLES.includes(user?.role))
  );

  const profileTabs = useMemo(() => {
    const tabs = [
      { key: 'profile', label: 'Profile' },
      { key: 'certs', label: 'Certificates & Trainings' }
    ];
    if (canViewCrewHours) {
      tabs.push({ key: 'crewHours', label: 'Crew Hours' });
    }
    tabs.push({ key: 'remarks', label: 'Remarks' });
    return tabs;
  }, [canViewCrewHours]);

  const activeProfileTabKey = profileTabs[profileTab]?.key || 'profile';
  
  // Users can manage their own trainings, SUPER_ADMIN and ACCOUNTABLE_MANAGER can manage any
  const canManageTrainings = useMemo(() => {
    if (!data || data.status === 'REJECTED') {
      console.log('[DEBUG] canManageTrainings: false - data:', !!data, 'status:', data?.status);
      return false;
    }
    const canManage = isOwnProfile || [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role);
    console.log('[DEBUG] Training Management:', { 
      isOwnProfile, 
      userRole: user?.role, 
      canManage, 
      status: data.status 
    });
    return canManage;
  }, [isOwnProfile, user?.role, data]);

  const activeStep = useMemo(() => {
    if (!data?.status) return 0;
    const index = STATUS_STEPS.indexOf(data.status);
    if (index === -1) return STATUS_STEPS.length;
    return index + 1;
  }, [data]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/users/onboard/${id}`);
      setData(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch onboarding detail:', err);
      setError(err.response?.data?.message || 'Failed to load onboarding detail.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Debug: Log when data or user changes
  useEffect(() => {
    console.log('[DEBUG] OnboardingDetail - Data/User changed:', {
      hasData: !!data,
      hasUser: !!user,
      userId: user?.id,
      dataUserId: data?.userId,
      dataUserIdType: typeof data?.userId,
      dataUserIdId: data?.userId?._id,
      isOwnProfile
    });
  }, [data, user, isOwnProfile]);

  const handleFinalApproval = async () => {
    try {
      setActionError('');
      await axios.put(`/api/users/onboard/${id}/approve`);
      fetchDetail();
    } catch (err) {
      console.error('Approval error:', err);
      setActionError(err.response?.data?.message || 'Failed to approve onboarding.');
    }
  };

  const handleReject = async () => {
    try {
      setActionError('');
      await axios.put(`/api/users/onboard/${id}/reject`, { reason: rejectReason });
      setRejectDialogOpen(false);
      setRejectReason('');
      fetchDetail();
    } catch (err) {
      console.error('Reject error:', err);
      setActionError(err.response?.data?.message || 'Failed to reject onboarding.');
    }
  };

  const handleCompleteTraining = async () => {
    setCompletingTraining(true);
    try {
      await axios.put(`/api/users/onboard/${id}/complete-training`);
      fetchDetail();
    } catch (err) {
      console.error('Complete training error:', err);
      setActionError(err.response?.data?.message || 'Failed to mark training as complete.');
    } finally {
      setCompletingTraining(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate('/users/onboard')}>Back to Onboarding</Button>
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4">
            {data.userId?.firstName || data.firstName} {data.userId?.lastName || data.lastName}
          </Typography>
          <Typography color="text.secondary">{data.userId?.email || data.email}</Typography>
        </Box>
        <Chip label={STATUS_LABELS[data.status] || data.status} color="primary" />
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Status Progress</Typography>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STATUS_STEPS.map((statusKey) => (
            <Step key={statusKey} completed={STATUS_STEPS.indexOf(statusKey) < activeStep}>
              <StepLabel>{STATUS_LABELS[statusKey]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Stack direction="row" spacing={2} mt={3} flexWrap="wrap">
          <Chip label={`Role: ${data.requestedRole?.replace(/_/g, ' ')}`} />
          {data.requestedBy && (
            <Chip label={`Requested By: ${data.requestedBy.firstName} ${data.requestedBy.lastName}`} />
          )}
          {data.userId && (
            <Chip label={`User ID: ${data.userId.email}`} />
          )}
        </Stack>

        <Stack direction="row" spacing={2} mt={3} flexWrap="wrap">
          {[ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role) && (
            <Button 
              variant="outlined" 
              onClick={() => navigate('/users/onboard')}
            >
              Back to Onboarding
            </Button>
          )}
          {canApprove && (
            <Button variant="contained" color="primary" onClick={handleFinalApproval}>
              Final Approval
            </Button>
          )}
          {canReject && (
            <Button variant="outlined" color="error" onClick={() => setRejectDialogOpen(true)}>
              Reject
            </Button>
          )}
          {canManageTrainings && data.status === 'PROFILE_COMPLETE' && (
            <Button variant="contained" color="success" onClick={handleCompleteTraining} disabled={completingTraining}>
              {completingTraining ? 'Marking...' : 'Mark Training Complete'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Profile Information - Read-only for all users */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Tabs
          value={Math.min(profileTab, profileTabs.length - 1)}
          onChange={(_, value) => setProfileTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {profileTabs.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>

        {activeProfileTabKey === 'profile' && (
          <Box>
            <ProfileView data={data} />
            {profileUserId && (
              <ProfileSignature
                userId={profileUserId}
                signatureImageUrl={data.userId?.signatureImageUrl}
                readOnly={!canManageSignature}
                onUpdated={fetchDetail}
              />
            )}
            {isOwnProfile && data?.userId && (
              <Box mt={2}>
                <PasswordChange
                  userId={data.userId._id || data.userId || user?.id}
                  onPasswordChanged={fetchDetail}
                />
              </Box>
            )}
            {canCompleteProfile && !isOwnProfile && (
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>Complete Profile</Typography>
                <ProfileCompletion
                  onboardingId={data._id}
                  requestedRole={data.requestedRole}
                  initialValues={data.profileCompletion}
                  onCompleted={fetchDetail}
                />
              </Box>
            )}
          </Box>
        )}

        {activeProfileTabKey === 'certs' && (
          <Box>
            <CertificateManagement
              entityId={data._id}
              mode="onboarding"
              readOnly={!canManageCertificates}
              userRole={user?.role}
            />
            <Divider sx={{ my: 3 }} />
            <TrainingManagement
              entityId={data._id}
              mode="onboarding"
              readOnly={!canManageTrainings}
              userRole={user?.role}
            />
          </Box>
        )}

        {activeProfileTabKey === 'crewHours' && (
          <CrewHoursBoard
            selfCrewId={String(profileUserId)}
            displayTimezone={displayTimezone}
          />
        )}

        {activeProfileTabKey === 'remarks' && (
          <Stack spacing={1}>
            {(data.remarks || []).map((remark) => (
              <Paper key={remark._id} sx={{ p: 2 }} variant="outlined">
                <Typography variant="body2">{remark.content}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {remark.addedBy?.firstName} {remark.addedBy?.lastName} • {new Date(remark.addedAt).toLocaleString()}
                </Typography>
              </Paper>
            ))}
            {(!data.remarks || data.remarks.length === 0) && (
              <Typography variant="body2" color="text.secondary">No remarks recorded.</Typography>
            )}
          </Stack>
        )}
      </Paper>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Onboarding</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!rejectReason}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnboardingDetail;
